import {useRef, useState} from 'react';
import Anchor from './Anchor';
import BadgeList from './BadgeList';
import ContentBg from './ContentBg';
import ContentCardGradientMaskV4 from './ContentCardGradientMaskV4';
import ContentCardInfo from './ContentCardInfo';
import ContentThumbnail from './ContentThumbnail';
import LabelBadgeV4 from './LabelBadgeV4';
import {Page} from '../constant/page';
import {callIfFunc} from '../function';
import useIntersection from '../hook/useIntersection';
import useSplitAdultBadgeList from '../hook/useSplitAdultBadgeList';
import {IContentCardCommonProps, IContentProps} from '../type';
import {PageInfo} from '../util/PageRouter';

interface IProps extends IContentCardCommonProps {
  content: IContentProps;
  landingUrl?: string | PageInfo;
  onClick?: () => void;
}

export default function ContentCardBody({
  content,
  landingUrl,
  onClick,
  onIntersection,
  additionalElement,
  beforeElement,
  className,
  cardInfoClassName,
  titleImageClassName,
  titleImageBottomElement,
  badgeClassName,
  bgScaleClassName,
  thumbClassName,
  maskClassName,
  maskStyle,
  isLazy = true,
}: IProps) {
  const {id, sid, badges, title, thumbImg, titleImg, bgImg, label} = content;
  const [showElement, setShowElement] = useState(!titleImg);
  const targetRef = useRef<HTMLAnchorElement>(null);
  const {adultBadges, otherBadges} = useSplitAdultBadgeList(badges);

  useIntersection({targetRef, onIntersection}, !onIntersection);

  return (
    <Anchor
      ref={targetRef}
      onClick={onClick}
      pageInfo={landingUrl || {page: Page.Content, id, sid}}
      className={cn(
        'w-full h-full relative',
        'overflow-hidden rounded-8',
        'before:absolute before:inset-0 before:bg-grey-01 before:-z-1',
        className
      )}
    >
      {showElement && beforeElement}
      {showElement && bgImg && (
        <ContentBg
          src={bgImg}
          bgScaleClassName={bgScaleClassName}
          pictureClassName="flex"
          isLazy={isLazy}
        />
      )}
      {showElement && thumbImg && (
        <ContentThumbnail
          src={thumbImg}
          className={thumbClassName}
          isLazy={isLazy}
        />
      )}
      <ContentCardGradientMaskV4
        bgColorRgb={content.bgColorRgb}
        className={maskClassName}
        style={maskStyle}
      />
      <div className="absolute left-0 top-0 mt-4 px-4">
        <div className="flex items-center gap-2">
          <BadgeList
            badges={otherBadges}
            badgeClassName={badgeClassName}
            className={cn((className = 'flex gap-x-2 !w-auto'))}
          />
          {label && label.text && <LabelBadgeV4 label={label} />}
          <BadgeList badges={adultBadges} badgeClassName={badgeClassName} />
        </div>
      </div>
      {callIfFunc(additionalElement, setShowElement)}
      <ContentCardInfo
        titleImg={titleImg}
        title={title}
        className={cardInfoClassName}
        badgeClassName={badgeClassName}
        titleImageClassName={titleImageClassName}
        titleImageBottomElement={titleImageBottomElement}
        onLoadTitleImage={setShowElement}
      />
    </Anchor>
  );
}
